import { buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { ErrorAlert } from "../components/ErrorAlert";
import { OpenerStyleSelect } from "../components/OpenerStyleSelect";
import { OpenerStylesPanel } from "../components/OpenerStylesPanel";
import { SettingRow } from "../components/SettingRow";
import { openerDefaultBadges } from "../lib/openerDefaults";
import { useOpenerStyleWrites } from "../lib/openerStyles";
import { useTRPC } from "../lib/trpc";

/**
 * Platform opener styles: the animations and theme overrides every tenant can open boxes with,
 * and the two defaults every environment inherits until its tenant picks one on its Openers
 * page: the storefront default (the hosted storefront and the API) and the Shopify default (a
 * Shopify shop's overlay, which follows the storefront default until staff pick one). Platform
 * admins only. A style is created and edited on a page of its own (`PlatformOpenerStylePage`).
 */
export function PlatformOpenerStylesPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const options = trpc.platform.openerStyles.list.queryOptions();
  const query = useQuery(options);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: options.queryKey });

  const { remove } = useOpenerStyleWrites("platform");
  const setDefault = useMutation(
    trpc.platform.openerStyles.setDefault.mutationOptions({
      onSuccess: () => {
        refresh();
        toast.success("Storefront default saved.");
      },
    }),
  );
  const setShopifyDefault = useMutation(
    trpc.platform.openerStyles.setShopifyDefault.mutationOptions({
      onSuccess: (saved) => {
        refresh();
        toast.success(
          saved.shopifyDefaultStyleId
            ? "Shopify default saved."
            : "Shopify is back on the storefront default.",
        );
      },
    }),
  );
  const data = query.data;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Opener styles</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          How boxes open: the animation and any colors a style overrides. Every tenant can use
          these, and the defaults are what every environment's boxes open with, on its storefront
          and in its Shopify shop, until its tenant picks another. A style follows each host's own
          theme wherever it leaves a color unset.
        </p>
      </div>
      {query.isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {query.error && <ErrorAlert message={query.error.message} />}
      {data && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Platform defaults</CardTitle>
              <CardDescription>
                New tenants start on them, and a change here reaches every environment that has not
                picked its own style.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid divide-y">
              <SettingRow
                htmlFor="platform-default-opener-style"
                label="Storefront"
                description="Hosted storefronts and the API: what a box opens with when neither it nor its environment names a style."
              >
                <OpenerStyleSelect
                  id="platform-default-opener-style"
                  styles={data.styles}
                  value={data.defaultStyleId}
                  disabled={setDefault.isPending}
                  onChange={(styleId) => styleId && setDefault.mutate({ styleId })}
                />
              </SettingRow>
              <SettingRow
                htmlFor="platform-shopify-default-opener-style"
                label="Shopify"
                description="A Shopify shop's overlay: what a box opens with when neither it, the shop nor its environment names a style."
              >
                <OpenerStyleSelect
                  id="platform-shopify-default-opener-style"
                  styles={data.styles}
                  value={data.shopifyDefaultStyleId}
                  inheritLabel="Same as the storefront"
                  inheritedStyleId={data.defaultStyleId}
                  disabled={setShopifyDefault.isPending}
                  onChange={(styleId) => setShopifyDefault.mutate({ styleId })}
                />
              </SettingRow>
            </CardContent>
          </Card>
          <section className="grid gap-4">
            <div>
              <h2 className="text-lg font-semibold tracking-tight">Styles</h2>
              <p className="text-sm text-muted-foreground">
                The classic spinner is what every box falls back to, so it cannot be deleted.
              </p>
            </div>
            <OpenerStylesPanel
              styles={data.styles}
              editableScope="platform"
              hostTheme={undefined}
              defaults={openerDefaultBadges({
                storefront: data.defaultStyleId,
                shopify: data.shopifyDefaultStyleId ?? data.defaultStyleId,
              })}
              canEdit
              editLink={(styleId) => (
                <Link
                  to="/opener-styles/$styleId"
                  params={{ styleId }}
                  className={buttonVariants({ size: "sm", variant: "outline" })}
                >
                  Edit
                </Link>
              )}
              newLink={
                <Link to="/opener-styles/new" className={buttonVariants()}>
                  New style
                </Link>
              }
              onDelete={(styleId) => remove.mutate({ styleId })}
            />
          </section>
        </>
      )}
    </div>
  );
}
